/**
 * Statistics Component
 * 
 * Displays helper assignment statistics per Fachbereich for administrators and Fachbereich Administrators.
 * Shows:
 * - Total demanded helpers (based on all position needs)
 * - Assigned helpers to Fachbereich (Zuordnungen Fachbereiche)
 * - Assigned helpers to Positions (Zuordnungen Positionen)
 * 
 * Table columns:
 * - Helferbedarf laut Positionen: Total required helpers based on all positions
 * - Zugeordnete Helfer: Number of helpers assigned to the Fachbereich
 * - Positionen mit Helfern: Number of helpers assigned to positions (clickable for Fachbereich Administrators)
 * - Status: Visual indicator based on assignment coverage (green/yellow/red)
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../constants';
import TrendChart, { type TrendChartData } from './TrendChart';
import './Statistics.css';
import './AdminHeader.css';

interface StatisticsData {
  fachbereich: string;
  demandedHelpers: number;
  assignedToFachbereich: number;
  assignedToPositions: number;
  totalAssignments?: number; // Total number of helper-position assignments (not distinct helpers)
}

interface TshirtStats {
  [key: string]: number;
}

interface StatisticsProps {
  onClose: () => void;
  token: string;
  onNavigateToHelperList?: (fachbereich?: string, filterKuchenspender?: boolean) => void;
  onNavigateToFachbereichPositions?: (fachbereich: string) => void;
}

const Statistics: React.FC<StatisticsProps> = ({ onClose, token, onNavigateToHelperList, onNavigateToFachbereichPositions }) => {
  const { year, user, isAdmin, isViewer } = useAuth();
  const canSeeTrends = true; // Visible for all users who can open Statistics
  const [statistics, setStatistics] = useState<StatisticsData[]>([]);
  const [totalRegisteredHelpers, setTotalRegisteredHelpers] = useState<number>(0);
  const [totalKuchenspenden, setTotalKuchenspenden] = useState<number>(0);
  const [tshirtStats, setTshirtStats] = useState<TshirtStats>({});
  const [totalTshirts, setTotalTshirts] = useState<number>(0);
  const [totalDemandedHours, setTotalDemandedHours] = useState<number>(0);
  const [totalAssignedHours, setTotalAssignedHours] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [trendsOverall, setTrendsOverall] = useState<TrendChartData | null>(null);
  const [trendsByFachbereich, setTrendsByFachbereich] = useState<Record<string, TrendChartData>>({});

  useEffect(() => {
    fetchStatistics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch trend data after statistics load (only for admins and Fachbereich admins)
  useEffect(() => {
    if (!token || loading || error || !canSeeTrends) return;
    const fetchTrends = async () => {
      const headers = { Authorization: `Bearer ${token}` };
      try {
        const [overallRes, ...fbReses] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/admin/statistics/trends`, { headers }),
          ...statistics.map((s) =>
            axios.get(`${API_BASE_URL}/api/admin/statistics/trends?fachbereich=${encodeURIComponent(s.fachbereich)}`, { headers })
          )
        ]);
        if (overallRes.data?.success) setTrendsOverall(overallRes.data);
        const byFb: Record<string, TrendChartData> = {};
        fbReses.forEach((r, i) => {
          if (r.data?.success && statistics[i]) byFb[statistics[i].fachbereich] = r.data;
        });
        setTrendsByFachbereich(byFb);
      } catch (err) {
        console.error('Error fetching trends:', err);
      }
    };
    fetchTrends();
  }, [token, loading, error, statistics, canSeeTrends]);

  // Handle Escape key to close dialog
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/api/admin/statistics`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setStatistics(response.data.statistics || []);
        setTotalRegisteredHelpers(response.data.totalRegisteredHelpers || 0);
        setTotalKuchenspenden(response.data.totalKuchenspenden || 0);
        setTshirtStats(response.data.tshirtStats || {});
        setTotalTshirts(response.data.totalTshirts || 0);
        setTotalDemandedHours(response.data.totalDemandedHours ?? 0);
        setTotalAssignedHours(response.data.totalAssignedHours ?? 0);
      } else {
        setError(response.data.message || 'Fehler beim Laden der Statistiken');
      }
    } catch (err: any) {
      console.error('Error fetching statistics:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Statistiken');
    } finally {
      setLoading(false);
    }
  };

  // More intuitive status calculation:
  // Compare total assignments (helper-position pairs) with total required assignments
  // This accounts for helpers being assigned to multiple positions
  const getStatusColor = (demanded: number, totalAssignments: number) => {
    if (demanded === 0) return '#999'; // No demand
    if (totalAssignments >= demanded) return '#28a745'; // Enough or more assignments
    if (totalAssignments >= demanded * 0.8) return '#ffc107'; // Close (80%+)
    return '#dc3545'; // Not enough
  };

  const getStatusText = (demanded: number, totalAssignments: number) => {
    if (demanded === 0) return 'Kein Bedarf';
    if (totalAssignments >= demanded) return 'Ausreichend';
    if (totalAssignments >= demanded * 0.8) return 'Fast ausreichend';
    return 'Unzureichend';
  };

  const totalDemanded = statistics.reduce((sum, stat) => sum + stat.demandedHelpers, 0);
  const totalAssignedToFachbereich = statistics.reduce((sum, stat) => sum + stat.assignedToFachbereich, 0);
  const totalAssignedToPositions = statistics.reduce((sum, stat) => sum + stat.assignedToPositions, 0);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for future export feature
  const exportToExcel = () => {
    if (statistics.length === 0) {
      alert('Keine Statistiken zum Exportieren vorhanden');
      return;
    }

    // Prepare Excel data
    const excelData: any[] = [];

    // Add summary row
    excelData.push({
      'Fachbereich': 'GESAMT',
      'Registrierte Helfer': totalRegisteredHelpers,
      'Bedarf (Positionen)': totalDemanded,
      'Zuordnungen Fachbereiche': totalAssignedToFachbereich,
      'Zuordnungen Positionen': totalAssignedToPositions,
      'Status': '',
      'Abdeckung (%)': totalDemanded > 0 ? Math.round((totalAssignedToPositions / totalDemanded) * 100) : 0,
      'Gesamtbedarf T-Shirts': totalTshirts,
      'Gesamt Kuchenspenden': totalKuchenspenden
    });

    // Add empty row for separation
    excelData.push({});

    // Add data rows
    statistics.forEach((stat) => {
      // Use totalAssignments if available, otherwise fall back to assignedToPositions for backward compatibility
      const totalAssignments = stat.totalAssignments !== undefined ? stat.totalAssignments : stat.assignedToPositions;
      const statusText = getStatusText(stat.demandedHelpers, totalAssignments);
      const percentage = stat.demandedHelpers > 0 
        ? Math.round((totalAssignments / stat.demandedHelpers) * 100) 
        : 0;

      excelData.push({
        'Fachbereich': stat.fachbereich,
        'Bedarf (Positionen)': stat.demandedHelpers,
        'Zuordnungen Fachbereiche': stat.assignedToFachbereich,
        'Zuordnungen Positionen': stat.assignedToPositions,
        'Status': statusText,
        'Abdeckung (%)': percentage
      });
    });

    // Add empty row for separation
    excelData.push({});

    // Add T-shirt statistics section
    excelData.push({
      'Fachbereich': 'T-SHIRT BEDARF NACH GRÖSSE',
      'Bedarf (Positionen)': '',
      'Zuordnungen Fachbereiche': '',
      'Zuordnungen Positionen': '',
      'Status': '',
      'Abdeckung (%)': '',
      'Gesamt Kuchenspenden': ''
    });

    // Add empty row
    excelData.push({});

    // Add T-shirt size rows
    ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'].forEach((size) => {
      excelData.push({
        'Fachbereich': `Größe ${size}`,
        'Bedarf (Positionen)': tshirtStats[size] || 0,
        'Zuordnungen Fachbereiche': '',
        'Zuordnungen Positionen': '',
        'Status': '',
        'Abdeckung (%)': '',
        'Gesamt Kuchenspenden': ''
      });
    });

    // Create Excel file
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Statistiken');

    // Set column widths
    const columnWidths = [
      { wch: 25 },  // Fachbereich
      { wch: 18 },  // Bedarf (Positionen)
      { wch: 25 },  // Zuordnungen Fachbereiche
      { wch: 22 },  // Zuordnungen Positionen
      { wch: 18 },  // Status
      { wch: 15 },  // Abdeckung (%)
      { wch: 18 },  // T-Shirts Bedarf
      { wch: 20 }   // Gesamt Kuchenspenden
    ];
    worksheet['!cols'] = columnWidths;

    // Format summary row (first row) - make it bold
    const summaryCellRef = XLSX.utils.encode_cell({ r: 0, c: 0 });
    if (!worksheet[summaryCellRef]) worksheet[summaryCellRef] = {};
    worksheet[summaryCellRef].s = {
      font: { bold: true },
      fill: { fgColor: { rgb: 'E0E0E0' } }
    };

    const today = new Date();
    const dateStr = today.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\//g, '-');
    const filename = `Roemerman_${year || 2026}_Statistiken_${dateStr}.xlsx`;

    XLSX.writeFile(workbook, filename);
  };

  return (
    <div className="statistics-overlay" onClick={onClose}>
      <div className="statistics-popup" onClick={(e) => e.stopPropagation()}>
        <div className="admin-header">
          <h2 className="admin-header-title">STATISTIKEN</h2>
          <button className="admin-header-close" onClick={onClose} title="Schließen">
            ×
          </button>
        </div>

        <div className="statistics-content">
          {loading ? (
            <div className="statistics-loading">Lädt...</div>
          ) : error ? (
            <div className="statistics-error">{error}</div>
          ) : (
            <>
              <div className="statistics-summary">
                <div className="statistics-summary-item">
                  <div className="statistics-summary-label">Registrierte Helfer</div>
                  <div 
                    className="statistics-summary-value statistics-clickable"
                    onClick={() => {
                      if (onNavigateToHelperList && totalRegisteredHelpers > 0) {
                        onClose();
                        onNavigateToHelperList();
                      }
                    }}
                    style={{ 
                      cursor: totalRegisteredHelpers > 0 ? 'pointer' : 'default',
                      textDecoration: totalRegisteredHelpers > 0 ? 'underline' : 'none'
                    }}
                    title={totalRegisteredHelpers > 0 ? 'Klicken, um Gesamthelferliste zu öffnen' : ''}
                  >
                    {totalRegisteredHelpers}
                  </div>
                </div>
                <div className="statistics-summary-item">
                  <div className="statistics-summary-label">Zuordnungen<br />Fachbereiche</div>
                  <div className="statistics-summary-value">{totalAssignedToFachbereich}</div>
                </div>
                <div className="statistics-summary-item">
                  <div className="statistics-summary-label">Gesamtbedarf Positionen</div>
                  <div className="statistics-summary-value-wrapper">
                    <div className="statistics-summary-value">{totalDemanded}</div>
                    <div className="statistics-summary-hours">
                      {totalDemandedHours.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} Std.
                    </div>
                  </div>
                </div>
                <div className="statistics-summary-item">
                  <div className="statistics-summary-label">Zuordnungen Positionen</div>
                  <div className="statistics-summary-value-wrapper">
                    <div className="statistics-summary-value">{totalAssignedToPositions}</div>
                    <div className="statistics-summary-hours">
                      {totalAssignedHours.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} Std.
                    </div>
                  </div>
                </div>
                <div className="statistics-summary-item">
                  <div className="statistics-summary-label statistics-summary-label-tshirts">
                    Gesamtbedarf<span className="statistics-summary-label-tshirts-break"> </span>T-Shirts
                  </div>
                  <div
                    className="statistics-summary-value statistics-clickable"
                    onClick={() => document.getElementById('statistics-tshirt-section')?.scrollIntoView({ behavior: 'smooth' })}
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    title="Klicken, um zum T-Shirt Bedarf zu scrollen"
                  >
                    {totalTshirts}
                  </div>
                </div>
                <div className="statistics-summary-item">
                  <div className="statistics-summary-label">Gesamt Kuchenspenden</div>
                  <div
                    className="statistics-summary-value statistics-clickable"
                    onClick={() => {
                      if (onNavigateToHelperList && totalKuchenspenden > 0) {
                        onClose();
                        onNavigateToHelperList(undefined, true);
                      }
                    }}
                    style={{
                      cursor: totalKuchenspenden > 0 ? 'pointer' : 'default',
                      textDecoration: totalKuchenspenden > 0 ? 'underline' : 'none'
                    }}
                    title={totalKuchenspenden > 0 ? 'Klicken, um Gesamthelferliste mit Kuchenspendern zu öffnen' : ''}
                  >
                    {totalKuchenspenden}
                  </div>
                </div>
              </div>

              {/* Overall trend chart - below summary on mobile, in trends section on desktop (admins and Fachbereich admins only) */}
              {canSeeTrends && (
                <div className="statistics-trend-overall-mobile">
                  {trendsOverall && (
                    <TrendChart data={trendsOverall} title="Gesamt – Registrierungen & Zuordnungen" compact size="overall" />
                  )}
                </div>
              )}

              {/* Desktop table */}
              <div className="statistics-table-container">
                <table className="statistics-table">
                  <thead>
                    <tr>
                      <th>Fachbereich</th>
                      <th>Helferbedarf laut Positionen</th>
                      <th>Zugeordnete Helfer</th>
                      <th>Positionen mit Helfern</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statistics.map((stat) => {
                      // Use totalAssignments if available, otherwise fall back to assignedToPositions for backward compatibility
                      const totalAssignments = stat.totalAssignments !== undefined ? stat.totalAssignments : stat.assignedToPositions;
                      const statusColor = getStatusColor(stat.demandedHelpers, totalAssignments);
                      const statusText = getStatusText(stat.demandedHelpers, totalAssignments);
                      // Check if user can navigate: admin of this Fachbereich OR global Administrator
                      const isMyFachbereich = user?.fachbereiche && user.fachbereiche.includes(stat.fachbereich);
                      const canNavigateToPositions = (isMyFachbereich || isAdmin() || isViewer()) && onNavigateToFachbereichPositions && stat.assignedToPositions > 0;
                      return (
                        <tr key={stat.fachbereich}>
                          <td className="statistics-fachbereich">{stat.fachbereich}</td>
                          <td className="statistics-number">{stat.demandedHelpers}</td>
                          <td 
                            className="statistics-number statistics-clickable"
                            onClick={() => {
                              if (onNavigateToHelperList && stat.assignedToFachbereich > 0) {
                                onClose();
                                onNavigateToHelperList(stat.fachbereich);
                              }
                            }}
                            style={{ 
                              cursor: stat.assignedToFachbereich > 0 ? 'pointer' : 'default',
                              textDecoration: stat.assignedToFachbereich > 0 ? 'underline' : 'none'
                            }}
                            title={stat.assignedToFachbereich > 0 ? `Klicken, um Helferliste mit Fachbereich "${stat.fachbereich}" zu öffnen` : ''}
                          >
                            {stat.assignedToFachbereich}
                          </td>
                          <td 
                            className={`statistics-number ${canNavigateToPositions ? 'statistics-clickable' : ''}`}
                            onClick={() => {
                              if (canNavigateToPositions) {
                                onClose();
                                onNavigateToFachbereichPositions(stat.fachbereich);
                              }
                            }}
                            style={{ 
                              cursor: canNavigateToPositions ? 'pointer' : 'default',
                              textDecoration: canNavigateToPositions ? 'underline' : 'none'
                            }}
                            title={canNavigateToPositions ? `Klicken, um Fachbereich Positionen für "${stat.fachbereich}" zu öffnen` : ''}
                          >
                            {stat.assignedToPositions}
                          </td>
                          <td>
                            <span 
                              className="statistics-status"
                              style={{ color: statusColor }}
                            >
                              {statusText}
                            </span>
                            {stat.demandedHelpers > 0 && (
                              <span className="statistics-percentage">
                                {' '}({Math.round((totalAssignments / stat.demandedHelpers) * 100)}%)
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile card layout */}
              <div className="statistics-table-container-mobile">
                {statistics.map((stat) => {
                  // Use totalAssignments if available, otherwise fall back to assignedToPositions for backward compatibility
                  const totalAssignments = stat.totalAssignments !== undefined ? stat.totalAssignments : stat.assignedToPositions;
                  const statusColor = getStatusColor(stat.demandedHelpers, totalAssignments);
                  const statusText = getStatusText(stat.demandedHelpers, totalAssignments);
                  // Check if user can navigate: admin of this Fachbereich OR global Administrator
                  const isMyFachbereich = user?.fachbereiche && user.fachbereiche.includes(stat.fachbereich);
                  const canNavigateToPositions = (isMyFachbereich || isAdmin() || isViewer()) && onNavigateToFachbereichPositions && stat.assignedToPositions > 0;
                  return (
                    <div key={stat.fachbereich} className="statistics-card">
                      <div className="statistics-card-header">{stat.fachbereich}</div>
                      <div className="statistics-card-row">
                        <span className="statistics-card-label">Helferbedarf laut Positionen</span>
                        <span className="statistics-card-value">{stat.demandedHelpers}</span>
                      </div>
                      <div className="statistics-card-row">
                        <span className="statistics-card-label">Zugeordnete Helfer</span>
                        <span 
                          className="statistics-card-value statistics-clickable"
                          onClick={() => {
                            if (onNavigateToHelperList && stat.assignedToFachbereich > 0) {
                              onClose();
                              onNavigateToHelperList(stat.fachbereich);
                            }
                          }}
                          style={{ 
                            cursor: stat.assignedToFachbereich > 0 ? 'pointer' : 'default',
                            textDecoration: stat.assignedToFachbereich > 0 ? 'underline' : 'none'
                          }}
                          title={stat.assignedToFachbereich > 0 ? `Klicken, um Helferliste mit Fachbereich "${stat.fachbereich}" zu öffnen` : ''}
                        >
                          {stat.assignedToFachbereich}
                        </span>
                      </div>
                      <div className="statistics-card-row">
                        <span className="statistics-card-label">Positionen mit Helfern</span>
                        <span 
                          className={`statistics-card-value ${canNavigateToPositions ? 'statistics-clickable' : ''}`}
                          onClick={() => {
                            if (canNavigateToPositions) {
                              onClose();
                              onNavigateToFachbereichPositions(stat.fachbereich);
                            }
                          }}
                          style={{ 
                            cursor: canNavigateToPositions ? 'pointer' : 'default',
                            textDecoration: canNavigateToPositions ? 'underline' : 'none'
                          }}
                          title={canNavigateToPositions ? `Klicken, um Fachbereich Positionen für "${stat.fachbereich}" zu öffnen` : ''}
                        >
                          {stat.assignedToPositions}
                        </span>
                      </div>
                      <div className="statistics-card-row">
                        <span className="statistics-card-label">Status</span>
                        <span className="statistics-card-value">
                          <span 
                            className="statistics-card-status"
                            style={{ color: statusColor }}
                          >
                            {statusText}
                          </span>
                          {stat.demandedHelpers > 0 && (
                            <span className="statistics-percentage">
                              {' '}({Math.round((totalAssignments / stat.demandedHelpers) * 100)}%)
                            </span>
                          )}
                        </span>
                      </div>
                      {/* Per-Fachbereich trend chart - mobile only (admins and Fachbereich admins only) */}
                      {canSeeTrends && trendsByFachbereich[stat.fachbereich] && (
                        <div className="statistics-card-trend">
                          <TrendChart
                            data={trendsByFachbereich[stat.fachbereich]}
                            title={`${stat.fachbereich} – Trend`}
                            compact
                            size="compact"
                            assignmentsLinearRamp
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="statistics-legend">
                <div className="statistics-legend-item">
                  <span className="statistics-legend-color" style={{ color: '#28a745' }}>●</span>
                  <span>Ausreichend (100%+ des Bedarfs)</span>
                </div>
                <div className="statistics-legend-item">
                  <span className="statistics-legend-color" style={{ color: '#ffc107' }}>●</span>
                  <span>Fast ausreichend (80-99% des Bedarfs)</span>
                </div>
                <div className="statistics-legend-item">
                  <span className="statistics-legend-color" style={{ color: '#dc3545' }}>●</span>
                  <span>Unzureichend (&lt;80% des Bedarfs)</span>
                </div>
              </div>

              {/* Desktop: trends section at end of screen (admins and Fachbereich admins only) */}
              {canSeeTrends && (
              <div className="statistics-trends-section">
                {trendsOverall && (
                  <div className="statistics-trends-overall">
                    <TrendChart data={trendsOverall} title="Gesamt – Registrierungen & Zuordnungen" size="overall" />
                  </div>
                )}
                {statistics.length > 0 && (
                  <div className="statistics-trends-fachbereiche">
                    {statistics.map((stat) =>
                      trendsByFachbereich[stat.fachbereich] ? (
                        <div key={stat.fachbereich} className="statistics-trends-fb-item">
                          <TrendChart
                            data={trendsByFachbereich[stat.fachbereich]}
                            title={stat.fachbereich}
                            size="fb"
                            assignmentsLinearRamp
                          />
                        </div>
                      ) : null
                    )}
                  </div>
                )}
              </div>
              )}

              <div id="statistics-tshirt-section" className="statistics-tshirt-section">
                <h3 className="statistics-tshirt-title">T-Shirt Bedarf nach Größe</h3>
                <div className="statistics-tshirt-table-container">
                  <table className="statistics-tshirt-table">
                    <thead>
                      <tr>
                        <th>Größe</th>
                        <th>Anzahl</th>
                      </tr>
                    </thead>
                    <tbody>
                      {['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'].map((size) => (
                        <tr key={size}>
                          <td className="statistics-tshirt-size">{size}</td>
                          <td className="statistics-tshirt-count">{tshirtStats[size] || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="statistics-tshirt-summary">
                        <td className="statistics-tshirt-size">Gesamt</td>
                        <td className="statistics-tshirt-count">{totalTshirts}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Statistics;

